/**
 * ChunkReloadBoundary — auto-recovers a stale tab after a deploy.
 *
 * When a new build is deployed, the old hashed JS/CSS chunks are removed from
 * the server. A tab still running the old bundle that then tries to load a chunk
 * it hasn't fetched yet (a lazily-imported route, a dynamic import, etc.) gets a
 * `ChunkLoadError` / "Loading chunk failed" → white screen. The correct recovery
 * is a full reload, which pulls the fresh index.html and the new chunk hashes.
 *
 * Two entry points are covered:
 *   1. Errors thrown during render (React.lazy) → this error boundary.
 *   2. Unhandled promise rejections from dynamic import() → installChunkErrorReload.
 *
 * A sessionStorage flag prevents a reload loop if the failure is not actually a
 * stale-chunk issue (e.g. the chunk is genuinely broken on the server).
 */
import React from 'react';

const RELOAD_GUARD_KEY = 'klaris_chunk_reload_at';
const RELOAD_LOOP_WINDOW_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: string }).name ?? '';
  const message = (error as { message?: string }).message ?? String(error);
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

/** Reload once; if we just reloaded moments ago, stop to avoid an infinite loop. */
function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_LOOP_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode, etc.) — still reload once.
  }
  window.location.reload();
  return true;
}

/**
 * Installs a global listener that reloads on stale-chunk promise rejections
 * (dynamic import() failures that never reach the render-path boundary).
 * Call once at startup. Returns a cleanup function.
 */
export function installChunkErrorReload(): () => void {
  const onRejection = (e: PromiseRejectionEvent) => {
    if (isChunkLoadError(e.reason)) {
      e.preventDefault();
      reloadOnce();
    }
  };
  window.addEventListener('unhandledrejection', onRejection);
  return () => window.removeEventListener('unhandledrejection', onRejection);
}

interface Props {
  children: React.ReactNode;
}

interface State {
  error: unknown;
}

export default class ChunkReloadBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown) {
    // Fire the reload as a side effect (getDerivedStateFromError must stay pure).
    if (isChunkLoadError(error)) reloadOnce();
  }

  render() {
    const { error } = this.state;
    if (error) {
      // Not a stale-chunk error → re-throw so an outer boundary / the default
      // React overlay can handle it. We only own chunk-load recovery.
      if (!isChunkLoadError(error)) throw error;
      // Chunk error: componentDidCatch has already requested a reload. If the
      // loop guard suppressed it, show a manual fallback instead of a blank page.
      return (
        <div style={{ padding: 24, fontFamily: 'Inter, sans-serif', textAlign: 'center' }}>
          <p style={{ marginBottom: 12 }}>A new version was just released.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#5e4db2', color: '#fff', cursor: 'pointer' }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
